import React, { useCallback, useEffect, useState } from 'react';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { documentDesignService } from '../../services/documentDesignService';
import PhotoPicker from './PhotoPicker';

const SOURCE_LABELS = { auto: 'Auto-matched', user: 'Chosen', none: 'No photo' };
const dpiClass = (dpi) => (!dpi ? 'unknown' : dpi >= 300 ? 'good' : dpi >= 200 ? 'fair' : 'low');

const LayoutView = ({ documentId, versionNo }) => {
  const [layout, setLayout] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [picking, setPicking] = useState(null);
  const [subtitle, setSubtitle] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await documentDesignService.getLayout(documentId, { version: versionNo || undefined });
      setLayout(data);
      setSubtitle(data.options.subtitle || '');
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load layout');
    }
  }, [documentId, versionNo]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (body, key) => {
    setBusy(key);
    try {
      setLayout(await documentDesignService.updateLayout(documentId, body));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const setOption = (key, value) => update({ action: 'options', options: { [key]: value } }, `opt:${key}`);

  if (!layout) {
    return error ? <div className="dd-error-inline">{error}</div> : <div className="loading-container"><Spinner size="large" /></div>;
  }

  const { options, slots, auto } = layout;
  const filled = slots.filter((s) => s.photo).length;

  return (
    <>
      {error && <div className="dd-error-inline">{error}</div>}
      <div className="dd-layout-options">
        <label className="dd-inline-check">
          <input type="checkbox" checked={options.section_openers !== 'none'} disabled={busy === 'opt:section_openers'}
            onChange={(e) => setOption('section_openers', e.target.checked ? 'divider' : 'none')} />
          Photo divider page before each chapter
        </label>
        <label className="dd-inline-check">
          <input type="checkbox" checked={!!options.require_approved_photos} disabled={busy === 'opt:require_approved_photos'}
            onChange={(e) => setOption('require_approved_photos', e.target.checked)} />
          Only photos approved for external use
        </label>
        <label className="dd-inline-check">
          <input type="checkbox" checked={!!options.include_toc} onChange={(e) => setOption('include_toc', e.target.checked)} />
          Table of contents
        </label>
        <label className="dd-inline-check">
          <input type="checkbox" checked={!!options.include_lof} onChange={(e) => setOption('include_lof', e.target.checked)} />
          List of figures
        </label>
        <label className="dd-inline-check">
          <input type="checkbox" checked={!!options.include_lot} onChange={(e) => setOption('include_lot', e.target.checked)} />
          List of tables
        </label>
        <label className="dd-layout-subtitle">
          Cover subtitle
          <input
            value={subtitle}
            placeholder="Defaults to the report type"
            onChange={(e) => setSubtitle(e.target.value)}
            onBlur={() => subtitle !== (options.subtitle || '') && setOption('subtitle', subtitle.trim() || null)}
          />
        </label>
      </div>

      <div className="dd-results-header">
        <h2>Photos <span className="dd-muted">· {filled} of {slots.length} slots filled{auto?.semantic === false && auto?.assigned ? ' · matched by quality (AI search unavailable)' : ''}</span></h2>
        <Button size="sm" variant="secondary" loading={busy === 'auto'} onClick={() => update({ action: 'auto' }, 'auto')}>
          Re-match automatic picks
        </Button>
      </div>
      {filled < slots.length && (
        <p className="dd-muted">
          Empty slots had no suitable Brand Library photo{options.require_approved_photos ? ' approved for external use' : ''} at print
          resolution (about 1275×1080 px for the cover). Uncheck &quot;Only photos approved for external use&quot;, add larger photos, or
          choose one manually. Open <strong>Preview &amp; PDF</strong> after slots are filled — cover and chapter divider pages use these picks.
        </p>
      )}

      <div className="dd-slot-grid">
        {slots.map((s) => (
          <div key={s.slot_key} className={`dd-slot ${s.kind}`}>
            <div className="dd-slot-photo">
              {s.photo?.web_url || s.photo?.thumb_url ? (
                <img src={s.photo.web_url || s.photo.thumb_url} alt={s.photo.description || s.photo.file_name} loading="lazy" />
              ) : (
                <span>{s.source === 'none' ? 'No photo (by choice)' : 'No photo'}</span>
              )}
              <div className="dd-media-badges">
                <span className="dd-badge cover">{s.kind === 'cover' ? 'Cover' : 'Chapter opener'}</span>
                {s.source && <span className="dd-badge muted">{SOURCE_LABELS[s.source]}</span>}
                {s.photo?.slot_dpi ? <span className={`dd-badge dpi-${dpiClass(s.photo.slot_dpi)}`}>{s.photo.slot_dpi} dpi</span> : null}
                {s.photo && !s.photo.approved_external && <span className="dd-badge flagged">Not approved external</span>}
              </div>
            </div>
            <div className="dd-slot-body">
              <div className="dd-slot-label" title={s.label}>{s.label}</div>
              {s.photo && <div className="dd-muted dd-slot-file" title={s.photo.description || ''}>{s.photo.file_name}</div>}
              <div className="dd-slot-actions">
                <Button size="sm" onClick={() => setPicking(s)}>Choose…</Button>
                {s.source !== 'none' && (
                  <Button size="sm" variant="ghost" loading={busy === s.slot_key}
                    onClick={() => update({ action: 'set', slot_key: s.slot_key, media_asset_id: null }, s.slot_key)}>
                    No photo
                  </Button>
                )}
                {s.source && s.source !== 'auto' && (
                  <Button size="sm" variant="ghost" onClick={() => update({ action: 'clear', slot_key: s.slot_key }, s.slot_key)}>
                    Reset to auto
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {picking && (
        <PhotoPicker
          documentId={documentId}
          versionNo={versionNo}
          slot={picking}
          onClose={() => setPicking(null)}
          onPick={async (assetId) => {
            const slot = picking;
            setPicking(null);
            await update({ action: 'set', slot_key: slot.slot_key, media_asset_id: assetId }, slot.slot_key);
          }}
        />
      )}
    </>
  );
};

export default LayoutView;
